import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import {
  Box,
  TextField,
  Button,
  IconButton,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tabs,
  Tab,
  useTheme,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Chip,
  CircularProgress,
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import CachedIcon from '@mui/icons-material/Cached';
import { END_POINTS } from '../../../shared';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { toast } from 'react-toastify';
import axiosInstance from '../../../axios/axios';
import { statusLabelMap, getAgentStatusColor } from './agentStatusDisplay';
import useRolePermissions from '../../../shared/hooks/useRolePermissions';

interface AddAgentWizardProps {
  onClose: () => void;
  onSubmit: (data: any) => void;
  selectedAgent?: any;
}

const AddAgentWizard = forwardRef<any, AddAgentWizardProps>(({ onClose, onSubmit, selectedAgent }, ref) => {
  useImperativeHandle(ref, () => ({
    handleClose
  }));

  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const { hasPermissions } = useRolePermissions();
  const canEditAgent = hasPermissions("create_edit_agents_new") || hasPermissions("create_edit_agent_new");

  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    agentName: '',
    description: '',
  });
  const [touched, setTouched] = useState({
    agentName: false,
    description: false,
  });
  const [selectedTab, setSelectedTab] = useState(0);
  const [expandedAccordions, setExpandedAccordions] = useState<Record<string, boolean>>({
    details: true,
    install: false,
    confirm: false,
  });
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [agentData, setAgentData] = useState<any>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [isRefreshingEnrollment, setIsRefreshingEnrollment] = useState(false);

  useEffect(() => {
    if (selectedAgent) {
      setFormData({
        agentName: selectedAgent.name || '',
        description: selectedAgent.description || '',
      });
      setAgentData(selectedAgent);
      // If it's already pending, we probably want to skip to install step
      if (selectedAgent.status_display === 'Pending' || selectedAgent.otp) {
        setActiveStep(1);
        setExpandedAccordions({
          details: false,
          install: true,
          confirm: false,
        });
      }
    } else {
      // Reset if no agent is selected (new agent flow)
      setFormData({ agentName: '', description: '' });
      setAgentData(null);
      setActiveStep(0);
      setExpandedAccordions({ details: true, install: false, confirm: false });
    }
  }, [selectedAgent]);

  const alphaNumPattern = /^[a-zA-Z0-9 ]*$/;

  const isStep1Valid = () => {
    return (
      formData.agentName.trim().length >= 5 &&
      formData.agentName.trim().length <= 18 &&
      alphaNumPattern.test(formData.agentName) &&
      formData.description.trim().length >= 5 &&
      formData.description.trim().length <= 50 &&
      alphaNumPattern.test(formData.description)
    );
  };

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleBlur = (field: string) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  const handleContinue = async () => {
    if (activeStep === 0 && isStep1Valid()) {
      setIsLoading(true);
      try {
        const payload = {
          name: formData.agentName,
          description: formData.description,
          agent_type: ["cloud", "on_prem"]
        };
        const response = await axiosInstance.post(END_POINTS.ADD_AGENT, payload);
        if (response.status === 200 || response.status === 201) {
          setAgentData(response.data.data);
          toast.success('Agent added successfully');
          onSubmit(response.data.data);
          setActiveStep(1);
          setExpandedAccordions((prev) => ({ ...prev, install: true }));
        }
      } catch (error: any) {
        toast.error(error.response?.data?.message || 'Failed to add agent');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleCopyCommand = (command: string) => {
    navigator.clipboard.writeText(command);
    setCopySuccess(true);
    setTimeout(() => {
      setCopySuccess(false);
    }, 2000);
  };

  const handleRefresh = async () => {
    const agentId = agentData?.id ?? selectedAgent?.id;
    if (!agentId) {
      toast.warning('No agent to refresh');
      return;
    }
    setIsRefreshingEnrollment(true);
    try {
      const response = await axiosInstance.get(END_POINTS.CLOUD_AGENT);
      if (response?.status === 200) {
        const list = response.data?.data || [];
        const updated = list.find((a: { id: string }) => a.id === agentId);
        if (updated) {
          setAgentData(updated);
          setIsEnrolled(updated.status_display === 'Active');
          onSubmit(updated);
          toast.success('Enrollment status updated');
        } else {
          toast.warning('Agent not found in the latest list');
        }
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to refresh enrollment status');
    } finally {
      setIsRefreshingEnrollment(false);
    }
  };

  const resetAndClose = () => {
    setActiveStep(0);
    setFormData({ agentName: '', description: '' });
    setTouched({ agentName: false, description: false });
    setExpandedAccordions({ details: true, install: false, confirm: false });
    setIsEnrolled(false);
    setAgentData(null);
    onClose();
  };

  const handleConfirmClose = (confirm: boolean) => {
    setConfirmDialogOpen(false);
    if (confirm) {
      resetAndClose();
    }
  };

  const handleClose = () => {
    if (activeStep === 0 && (formData.agentName.trim() || formData.description.trim())) {
      setConfirmDialogOpen(true);
    } else {
      resetAndClose();
    }
  };

  const handleDownloadTemplate = () => {
    const templateUrl = agentData?.cloud_formation_template || selectedAgent?.cloud_formation_template;
    if (templateUrl) {
      window.open(templateUrl, "_blank");
    }
  };

  const enrollmentStatusDisplay =
    activeStep >= 1 ? (agentData?.status_display ?? selectedAgent?.status_display ?? 'Pending') : '';

  const installTabs = [
    { label: 'Linux Tar', value: 0 },
    // { label: 'Mac', value: 1 },
    // { label: 'Windows', value: 2 },
    // { label: 'RPM', value: 3 },
    // { label: 'DEB', value: 4 },
    // { label: 'Kubernetes', value: 5 },
    // { label: 'CFT', value: 6 },
    { label: 'AWS', value: 1 },
  ];

  const getInstallCommand = () => {
    if (selectedTab === 0) {
      return agentData?.spark_agent_op_cmd || '';
    }
    return '';
  };

  // ==================== SHARED STYLES ====================
  const accordionSx = {
    boxShadow: 'none',
    bgcolor: 'transparent',
    margin: 0,
    border: 'none',

    '&:before': {
      display: 'none', // removes top divider line
    },

    '&.Mui-expanded': {
      margin: 0,
    },
  };

  const accordionSummarySx = {
    px: 0,
    minHeight: '52px !important',
    '& .MuiAccordionSummary-content': {
      alignItems: 'center',
      gap: 1.5,
      my: '10px !important',
    },
    '& .MuiAccordionSummary-expandIconWrapper': {
      color: isDarkMode ? '#aaa' : '#555',
    },
  };

  const sectionTitleSx = {
    fontWeight: 600,
    fontSize: '16px',
    color: isDarkMode ? '#e0e0e0' : '#1a1a2e',
    fontFamily: 'inherit',
  };

  const textFieldSx = {
    mb: 2.5,
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
      fontSize: '15px',
      '& fieldset': {
        borderColor: isDarkMode ? '#444' : '#d1d5db',
      },
      '&:hover fieldset': {
        borderColor: isDarkMode ? '#666' : '#9ca3af',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#4640DE',
        borderWidth: '1.5px',
      },
    },
    '& .MuiInputLabel-root': {
      fontSize: '14px',
      color: isDarkMode ? '#999' : '#6b7280',
      '&.Mui-focused': {
        color: '#4640DE',
      },
    },
    '& .MuiOutlinedInput-input': {
      py: '14px',
      px: '16px',
    },
  };

  // Step dot with Figma matching size and style
  const stepDot = (active: boolean, completed: boolean) => (
    <Box
      sx={{
        width: 24,
        height: 24,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        transition: 'all 0.2s ease',
      }}
    >
      {completed ? (
        <Box
          sx={{
            width: 12,
            height: 12,
            borderRadius: '50%',
            bgcolor: '#4640DE',
          }}
        />
      ) : active ? (
        <Box
          sx={{
            width: 24,
            height: 24,
            borderRadius: '50%',
            bgcolor: '#4640DE',
          }}
        />
      ) : (
        <Box
          sx={{
            width: 12,
            height: 12,
            borderRadius: '50%',
            bgcolor: isDarkMode ? '#444' : '#d1d5db',
          }}
        />
      )}
    </Box>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        bgcolor: isDarkMode ? '#1a1a2e' : '#fff',
        fontFamily: '"Inter", "Manrope", sans-serif',
      }}
    >
      {/* ==================== HEADER ==================== */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 3.5,
          py: 2.5,
          borderBottom: `1px solid ${isDarkMode ? '#2d2d44' : '#e5e7eb'}`,
          flexShrink: 0,
        }}
      >
        <Typography
          sx={{
            fontSize: '16px',
            fontWeight: 700,
            color: isDarkMode ? '#f0f0f0' : '#111827',
            letterSpacing: '-0.3px',
          }}
        >
          Add Agent
        </Typography>
        <IconButton
          onClick={handleClose}
          sx={{
            p: 0,
            color: isDarkMode ? '#777' : '#9ca3af',
            '&:hover': { color: isDarkMode ? '#bbb' : '#6b7280' },
          }}
        >
          <CancelIcon sx={{ fontSize: 22 }} />
        </IconButton>
      </Box>

      {/* ==================== SCROLLABLE BODY ==================== */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          px: 3.5,
          py: 1,
          '&::-webkit-scrollbar': { width: 6 },
          '&::-webkit-scrollbar-thumb': {
            bgcolor: isDarkMode ? '#555' : '#ccc',
            borderRadius: 3,
          },
        }}
      >
        {/* ==================== SECTION 1: Details ==================== */}
        <Accordion
          expanded={!!expandedAccordions.details}
          onChange={() => setExpandedAccordions((prev) => ({ ...prev, details: !prev.details }))}
          sx={accordionSx}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx}>
            {/* {stepDot(activeStep >= 1, activeStep > 1)} */}
            {stepDot(activeStep === 0, activeStep > 0)}
            <Typography sx={sectionTitleSx}>Details</Typography>
          </AccordionSummary>

          <AccordionDetails sx={{ px: 0, pt: 0, pb: 1, position: 'relative' }}>
            <Box
              sx={{
                position: 'absolute',
                left: 11,
                top: 0,
                bottom: 0,
                width: 2,
                bgcolor: isDarkMode ? '#444' : '#d1d5db',
                zIndex: 0,
              }}
            />
            <Box sx={{ pl: 5 }}>
              <TextField
                label="Agent Name"
                placeholder="Enter agent name"
                value={formData.agentName}
                onChange={(e) => handleChange('agentName', e.target.value)}
                onBlur={() => handleBlur('agentName')}
                fullWidth
                required
                inputProps={{ maxLength: 18 }}
                error={
                  touched.agentName &&
                  (formData.agentName.trim().length < 5 ||
                    formData.agentName.trim().length > 18 ||
                    !alphaNumPattern.test(formData.agentName))
                }
                helperText={
                  touched.agentName &&
                  (formData.agentName.trim().length < 5
                    ? 'Min 5 characters required'
                    : formData.agentName.trim().length > 18
                      ? 'Max 18 characters allowed'
                      : !alphaNumPattern.test(formData.agentName)
                        ? 'Only alphanumeric characters allowed'
                        : '')
                }
                sx={textFieldSx}
              />
              <TextField
                label="Description"
                placeholder="Enter description"
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                onBlur={() => handleBlur('description')}
                fullWidth
                required
                inputProps={{ maxLength: 50 }}
                error={
                  touched.description &&
                  (formData.description.trim().length < 5 ||
                    formData.description.trim().length > 50 ||
                    !alphaNumPattern.test(formData.description))
                }
                helperText={
                  touched.description &&
                  (formData.description.trim().length < 5
                    ? 'Min 5 characters required'
                    : formData.description.trim().length > 50
                      ? 'Max 50 characters allowed'
                      : !alphaNumPattern.test(formData.description)
                        ? 'Only alphanumeric characters allowed'
                        : '')
                }
                sx={textFieldSx}
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
                <Button
                  variant="contained"
                  onClick={handleContinue}
                  disabled={!isStep1Valid() || activeStep > 0 || isLoading}
                  sx={{
                    textTransform: 'none',
                    borderRadius: '8px',
                    px: 4,
                    py: 1,
                    fontSize: '14px',
                    fontWeight: 600,
                    bgcolor: '#4640DE',
                    boxShadow: 'none',
                    '&:hover': { bgcolor: '#3730CC', boxShadow: '0 2px 8px rgba(70, 64, 222, 0.3)' },
                    '&.Mui-disabled': {
                      bgcolor: isDarkMode ? '#2d2d44' : '#e5e7eb',
                      color: isDarkMode ? '#555' : '#9ca3af',
                    },
                  }}
                >
                  {isLoading ? 'Processing...' : 'Continue'}
                </Button>
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>
        {activeStep === 1 && (
          <Accordion
            expanded={!!expandedAccordions.install}
            onChange={() => {
              if (activeStep >= 1) {
                setExpandedAccordions((prev) => ({ ...prev, install: !prev.install }));
              }
            }}
            disabled={activeStep < 1}
            sx={{
              ...accordionSx,
              '&.Mui-disabled': {
                bgcolor: 'transparent',
                opacity: activeStep < 1 ? 0.45 : 1,
              },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx}>
              {stepDot(activeStep >= 1, activeStep > 1)}
              <Typography sx={sectionTitleSx}>Install Agent on your host</Typography>
            </AccordionSummary>

            <AccordionDetails sx={{ px: 0, pt: 0, pb: 2 }}>
              <Box sx={{ pl: 5 }}>
                <Tabs
                  value={selectedTab}
                  onChange={(_, newValue) => setSelectedTab(newValue)}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{
                    mb: 2.5,
                    minHeight: 38,
                    borderBottom: `1px solid ${isDarkMode ? '#333' : '#e5e7eb'}`,
                    '& .MuiTab-root': {
                      textTransform: 'none',
                      fontSize: '14px',
                      fontWeight: 500,
                      minWidth: 'auto',
                      minHeight: 38,
                      px: 1.5,
                      py: 0.5,
                      color: isDarkMode ? '#888' : '#6b7280',
                      '&.Mui-selected': {
                        color: '#4640DE',
                        fontWeight: 600,
                      },
                    },
                    '& .MuiTabs-indicator': {
                      bgcolor: '#4640DE',
                      height: 2.5,
                      borderRadius: '2px 2px 0 0',
                    },
                  }}
                >
                  {installTabs.map((tab) => (
                    <Tab key={tab.value} label={tab.label} />
                  ))}
                </Tabs>

                {/* AWS Tab Content */}
                {selectedTab === 1 ? (
                  <Box>
                    {/* Download Template option */}
                    <Box
                      sx={{
                        border: `1px solid ${isDarkMode ? '#333' : '#e5e7eb'}`,
                        borderRadius: '10px',
                        p: 2.5,
                        mb: 2,
                        bgcolor: isDarkMode ? '#2d2d44' : '#F5F5FF',
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography sx={{ fontWeight: 600, fontSize: '15px', color: isDarkMode ? '#e0e0e0' : '#111827' }}>
                          Download Template option
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Tooltip title="Download Template" arrow>
                            <IconButton
                              size="small"
                              onClick={handleDownloadTemplate}
                              sx={{
                                bgcolor: '#4640DE',
                                color: '#fff',
                                width: 30,
                                height: 30,
                                '&:hover': { bgcolor: '#3730CC' },
                              }}
                            >
                              <DownloadIcon sx={{ fontSize: 17 }} />
                            </IconButton>
                          </Tooltip>
                          <IconButton size="small" sx={{ color: isDarkMode ? '#888' : '#9ca3af' }}>
                            <ExpandMoreIcon sx={{ fontSize: 20, transform: 'rotate(-90deg)' }} />
                          </IconButton>
                        </Box>
                      </Box>
                      <Box
                        component="ol"
                        sx={{
                          pl: 2.5,
                          m: 0,
                          '& li': {
                            fontSize: '13px',
                            color: isDarkMode ? '#bbb' : '#4b5563',
                            lineHeight: 1.9,
                            '& strong': { fontWeight: 700, color: isDarkMode ? '#ddd' : '#111827' },
                          },
                        }}
                      >
                        <li>Download the CloudFormation template.</li>
                        <li>Log in to your AWS account with admin permissions.</li>
                        <li>Navigate to the CloudFormation console.</li>
                        <li>Click on <strong>Create Stack</strong>.</li>
                        <li>Enter a name for the stack (e.g. <strong>Spark-Agent</strong>).</li>
                        <li>Copy the <strong>OTP</strong> and <strong>Agent ID</strong> from the Spark Agent Console.</li>
                        <li>Enter them in the CloudFormation stack parameters.</li>
                      </Box>
                    </Box>

                    {/* Go to AWS */}
                    <Box
                      sx={{
                        border: `1px solid ${isDarkMode ? '#333' : '#e5e7eb'}`,
                        borderRadius: '10px',
                        bgcolor: isDarkMode ? '#2d2d44' : '#F5F5FF',
                        px: 2.5,
                        py: 1.8,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Typography sx={{ fontWeight: 600, fontSize: '15px', color: isDarkMode ? '#e0e0e0' : '#111827' }}>
                        Go to AWS
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Tooltip title="Go To AWS" arrow>
                          <IconButton
                            size="small"
                            sx={{
                              bgcolor: '#4640DE',
                              color: '#fff',
                              width: 30,
                              height: 30,
                              '&:hover': { bgcolor: '#3730CC' },
                            }}
                            onClick={() => window.open(agentData?.redirection_url || 'https://console.aws.amazon.com/cloudformation', '_blank')}
                          >
                            <OpenInNewIcon sx={{ fontSize: 17 }} />
                          </IconButton>
                        </Tooltip>
                        <IconButton size="small" sx={{ color: isDarkMode ? '#888' : '#9ca3af' }}>
                          <ExpandMoreIcon sx={{ fontSize: 20, transform: 'rotate(-90deg)' }} />
                        </IconButton>
                      </Box>
                    </Box>
                  </Box>
                ) : (
                  /* Default command block for other tabs */
                  <Box
                    sx={{
                      bgcolor: isDarkMode ? '#111827' : '#F5F5FF  ',
                      border: `1px solid ${isDarkMode ? '#2d2d44' : '#e5e7eb'}`,
                      p: 2.5,
                      pr: 6,
                      borderRadius: '10px',
                      position: 'relative',
                      fontFamily: '"SF Mono", "Fira Code", "Consolas", monospace',
                      fontSize: '13px',
                      lineHeight: 1.8,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-all',
                      color: isDarkMode ? '#d1d5db' : '#374151',
                    }}
                  >
                    <Tooltip title={canEditAgent ? (copySuccess ? 'Copied!' : 'Copy to clipboard') : ''} arrow>
                      <IconButton
                        size="small"
                        onClick={() => {
                          if (canEditAgent) {
                            handleCopyCommand(getInstallCommand());
                          }
                        }}
                        sx={{
                          position: 'absolute',
                          top: 10,
                          right: 10,
                          bgcolor: isDarkMode ? '#1f2937' : '#fff',
                          border: `1px solid ${isDarkMode ? '#374151' : '#d1d5db'}`,
                          borderRadius: '6px',
                          width: 32,
                          height: 32,
                          cursor: canEditAgent ? 'pointer' : 'default',
                          pointerEvents: canEditAgent ? 'auto' : 'none',
                          opacity: canEditAgent ? 1 : (isDarkMode ? 0.5 : 0.6),
                          '&:hover': {
                            bgcolor: canEditAgent ? (isDarkMode ? '#374151' : '#f3f4f6') : (isDarkMode ? '#1f2937' : '#fff'),
                            borderColor: canEditAgent ? '#4640DE' : (isDarkMode ? '#374151' : '#d1d5db'),
                          },
                        }}
                      >
                        <ContentCopyIcon sx={{
                          fontSize: 15,
                          color: canEditAgent ? (isDarkMode ? '#9ca3af' : '#6b7280') : (isDarkMode ? '#ACA8FA' : '#C7C4FF')
                        }} />
                      </IconButton>
                    </Tooltip>
                    {agentData?.spark_agent_op_cmd || '—'}

                    {/* {getInstallCommand()} */}
                  </Box>
                )}

                {/* Agent ID and OTP Section Grouped (Always visible) */}
                <Box
                  sx={{
                    mt: 2.5,
                    p: 2.5,
                    bgcolor: isDarkMode ? '#2d2d4420' : '#F5F5FF',
                    border: `1px solid ${isDarkMode ? '#2d2d44' : '#e5e7eb'}`,
                    borderRadius: '12px',
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                    {/* OTP */}
                    <Box sx={{ flex: 1, minWidth: '200px' }}>
                      <Typography
                        sx={{
                          mb: 1,
                          fontWeight: 600,
                          fontSize: '14px',
                          color: isDarkMode ? '#e0e0e0' : '#111827',
                        }}
                      >
                        OTP
                      </Typography>
                      <Box
                        sx={{
                          bgcolor: isDarkMode ? '#111827' : '#fff',
                          border: `1px solid ${isDarkMode ? '#2d2d44' : '#e5e7eb'}`,
                          p: 1.5,
                          pr: 5,
                          borderRadius: '8px',
                          position: 'relative',
                          fontFamily: '"SF Mono", "Fira Code", "Consolas", monospace',
                          fontSize: '13px',
                          lineHeight: 1.5,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-all',
                          color: isDarkMode ? '#d1d5db' : '#374151',
                        }}
                      >
                        <Tooltip title={canEditAgent ? (copySuccess ? 'Copied!' : 'Copy to clipboard') : ''} arrow>
                          <IconButton
                            size="small"
                            onClick={() => {
                              if (canEditAgent) {
                                handleCopyCommand(agentData?.otp || '');
                              }
                            }}
                            sx={{
                              position: 'absolute',
                              top: '50%',
                              right: 8,
                              transform: 'translateY(-50%)',
                              bgcolor: isDarkMode ? '#1f2937' : '#fff',
                              border: `1px solid ${isDarkMode ? '#374151' : '#d1d5db'}`,
                              borderRadius: '6px',
                              width: 28,
                              height: 28,
                              cursor: canEditAgent ? 'pointer' : 'default',
                              pointerEvents: canEditAgent ? 'auto' : 'none',
                              opacity: canEditAgent ? 1 : (isDarkMode ? 0.5 : 0.6),
                              '&:hover': {
                                bgcolor: canEditAgent ? (isDarkMode ? '#374151' : '#f3f4f6') : (isDarkMode ? '#1f2937' : '#fff'),
                                borderColor: canEditAgent ? '#4640DE' : (isDarkMode ? '#374151' : '#d1d5db'),
                              },
                            }}
                          >
                            <ContentCopyIcon sx={{
                              fontSize: 14,
                              color: canEditAgent ? (isDarkMode ? '#9ca3af' : '#6b7280') : (isDarkMode ? '#ACA8FA' : '#C7C4FF')
                            }} />
                          </IconButton>
                        </Tooltip>
                        {agentData?.otp || '—'}
                      </Box>
                    </Box>
                    {/* Agent ID */}
                    <Box sx={{ flex: 1, minWidth: '200px' }}>
                      <Typography
                        sx={{
                          mb: 1,
                          fontWeight: 600,
                          fontSize: '14px',
                          color: isDarkMode ? '#e0e0e0' : '#111827',
                        }}
                      >
                        Agent ID
                      </Typography>
                      <Box
                        sx={{
                          bgcolor: isDarkMode ? '#111827' : '#fff',
                          border: `1px solid ${isDarkMode ? '#2d2d44' : '#e5e7eb'}`,
                          p: 1.5,
                          pr: 5,
                          borderRadius: '8px',
                          position: 'relative',
                          fontFamily: '"SF Mono", "Fira Code", "Consolas", monospace',
                          fontSize: '13px',
                          lineHeight: 1.5,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-all',
                          color: isDarkMode ? '#d1d5db' : '#374151',
                        }}
                      >
                        <Tooltip title={canEditAgent ? (copySuccess ? 'Copied!' : 'Copy to clipboard') : ''} arrow>
                          <IconButton
                            size="small"
                            onClick={() => {
                              if (canEditAgent) {
                                handleCopyCommand(agentData?.id || '');
                              }
                            }}
                            sx={{
                              position: 'absolute',
                              top: '50%',
                              right: 8,
                              transform: 'translateY(-50%)',
                              bgcolor: isDarkMode ? '#1f2937' : '#fff',
                              border: `1px solid ${isDarkMode ? '#374151' : '#d1d5db'}`,
                              borderRadius: '6px',
                              width: 28,
                              height: 28,
                              cursor: canEditAgent ? 'pointer' : 'default',
                              pointerEvents: canEditAgent ? 'auto' : 'none',
                              opacity: canEditAgent ? 1 : (isDarkMode ? 0.5 : 0.6),
                              '&:hover': {
                                bgcolor: canEditAgent ? (isDarkMode ? '#374151' : '#f3f4f6') : (isDarkMode ? '#1f2937' : '#fff'),
                                borderColor: canEditAgent ? '#4640DE' : (isDarkMode ? '#374151' : '#d1d5db'),
                              },
                            }}
                          >
                            <ContentCopyIcon sx={{
                              fontSize: 14,
                              color: canEditAgent ? (isDarkMode ? '#9ca3af' : '#6b7280') : (isDarkMode ? '#ACA8FA' : '#C7C4FF')
                            }} />
                          </IconButton>
                        </Tooltip>
                        {agentData?.id || '—'}
                      </Box>
                    </Box>


                  </Box>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>

        )}

        {/* ==================== SECTION 2: Install Agent ==================== */}


        {/* ==================== SECTION 3: Confirm Enrollment ==================== */}
        {activeStep === 1 && (
          <Accordion
            expanded={!!expandedAccordions.confirm}
            onChange={() => {
              if (activeStep >= 1) {
                setExpandedAccordions((prev) => ({ ...prev, confirm: !prev.confirm }));
              }
            }}
            disabled={activeStep < 1}
            sx={{
              ...accordionSx,
              '&.Mui-disabled': {
                bgcolor: 'transparent',
                opacity: activeStep < 1 ? 0.45 : 1,
              },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx}>
              {stepDot(activeStep >= 1, isEnrolled)}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', minWidth: 0 }}>
                <Typography sx={sectionTitleSx}>Confirm Agent enrollment</Typography>
                {enrollmentStatusDisplay ? (
                  <Chip
                    label={statusLabelMap[enrollmentStatusDisplay] || enrollmentStatusDisplay}
                    size="small"
                    sx={{
                      ...getAgentStatusColor(enrollmentStatusDisplay, isDarkMode),
                      fontSize: '13px',
                      height: '20px',
                      width: 'auto',
                      fontWeight: 600,
                      borderRadius: '4px',
                      px: 0.5,
                    }}
                  />
                ) : null}
              </Box>
              {activeStep >= 1 && (
                <Box
                  component="span"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (canEditAgent && !isRefreshingEnrollment) void handleRefresh();
                  }}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    ml: 1.5,
                    cursor: canEditAgent ? (isRefreshingEnrollment ? 'default' : 'pointer') : 'default',
                    color: canEditAgent ? '#4640DE' : (isDarkMode ? '#ACA8FA' : '#aca8faff'),
                    fontSize: '14px',
                    fontWeight: 600,
                    transition: 'all 0.15s ease',
                    opacity: canEditAgent ? (isRefreshingEnrollment ? 0.6 : 1) : (isDarkMode ? 0.5 : 0.6),
                    pointerEvents: canEditAgent ? (isRefreshingEnrollment ? 'none' : 'auto') : 'none',
                    '&:hover': { opacity: canEditAgent ? (isRefreshingEnrollment ? 0.6 : 0.8) : (isDarkMode ? 0.5 : 0.6) },
                  }}
                >
                  <Box
                    sx={{
                      width: 18,
                      height: 18,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    {isRefreshingEnrollment ? (
                      <CircularProgress size={18} thickness={4.5} sx={{ color: '#4640DE' }} />
                    ) : (
                      <CachedIcon sx={{ fontSize: 18, color: 'inherit' }} />
                    )}
                  </Box>
                  Refresh
                </Box>
              )}
            </AccordionSummary>

            <AccordionDetails sx={{ px: 0, pt: 0, pb: 2 }}>
              <Box sx={{ pl: 5 }}>
                if you're having trouble connectinig, check out the troubleshooting guide.
              </Box>
            </AccordionDetails>
          </Accordion>
        )}
        {activeStep > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, mb: 1 }}>
            <Button
              variant="contained"
              color="primary"
              sx={{
                width: 116,
                textTransform: 'none',
                borderRadius: '8px',
                fontWeight: 600,
                bgcolor: '#4640DE',
                '&:hover': { bgcolor: '#3730CC' }
              }}
              onClick={() => handleConfirmClose(true)}
            >
              Close
            </Button>
          </Box>
        )}
      </Box>


      {/* ==================== CONFIRMATION DIALOG ==================== */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => {
          setConfirmDialogOpen(false)
        }}
        PaperProps={{
          sx: {
            borderRadius: '12px',
            p: 1,
            bgcolor: isDarkMode ? '#1a1a2e' : '#fff',
            backgroundImage: 'none',
          }
        }}
      >
        <DialogTitle sx={{ textAlign: "center", fontWeight: 700, px: 3, pt: 3 }}>
          Unsaved Changes
        </DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            textAlign: "center",
            px: 4,
            py: 2,
            color: isDarkMode ? '#aaa' : '#666',
          }}
        >
          This action will cancel the Agent initiated.
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", px: 3, pb: 3, gap: 1.5 }}>
          <Button
            onClick={() => handleConfirmClose(true)}
            variant="contained"
            fullWidth
            sx={{
              bgcolor: '#4640DE',
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '8px',
              '&:hover': { bgcolor: '#3730CC' }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => handleConfirmClose(false)}
            variant="outlined"
            fullWidth
            sx={{
              borderColor: isDarkMode ? '#444' : '#d1d5db',
              color: isDarkMode ? '#aaa' : '#666',
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '8px',
              '&:hover': {
                borderColor: '#4640DE',
                color: '#4640DE',
                bgcolor: isDarkMode ? '#4640DE10' : '#F5F5FF'
              }
            }}
          >
            Go Back
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});

AddAgentWizard.displayName = 'AddAgentWizard';

export default AddAgentWizard;
